import React, { useState, useEffect } from 'react';
import {
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Box,
    IconButton,
    Tooltip,
    Avatar,
    Stack
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Person as PersonIcon
} from '@mui/icons-material';
import { useApi } from '../hooks/useApi';
import moment from 'moment-timezone';

const OverrideHistory = ({ title = 'Override Geschiedenis', limit = 10 }) => {
    const [overrides, setOverrides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { get } = useApi();

    const fetchOverrides = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await get('/override', {
                filter: JSON.stringify({}),
                range: JSON.stringify([0, limit - 1]),
                sort: JSON.stringify(['timestamp', 'DESC'])
            });

            if (response?.data?.data) {
                setOverrides(response.data.data);
            } else {
                setOverrides([]);
            }
        } catch (err) {
            console.error('Fout bij ophalen override geschiedenis:', err);
            setError('Kon override geschiedenis niet laden');
            setOverrides([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOverrides();
    }, [limit]);

    const formatDateTime = (dateTimeString) => {
        return moment(dateTimeString).tz('Europe/Amsterdam').format('DD-MM-YYYY HH:mm:ss');
    };

    const getStateColor = (state) => {
        switch (state) {
            case 'on':
                return 'success';
            case 'off':
                return 'error';
            case 'auto':
                return 'info';
            default:
                return 'default';
        }
    };

    const getStateLabel = (state) => {
        switch (state) {
            case 'on':
                return 'Aan';
            case 'off':
                return 'Uit';
            case 'auto':
                return 'Auto';
            default:
                return state;
        }
    };

    const getUserInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(word => word[0]).join('').toUpperCase();
    };

    const getUserDisplayName = (override) => {
        if (override.user_name) {
            return override.user_name;
        }
        if (override.user_email) {
            return override.user_email;
        }
        if (override.user_id) {
            return `User ID: ${override.user_id}`;
        }
        return 'Systeem';
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    {title}
                </Typography>
                <Tooltip title="Vernieuwen">
                    <IconButton onClick={fetchOverrides} disabled={loading}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Tijdstip</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Gebruiker</TableCell>
                            <TableCell>Aangemaakt</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    <Typography>Laden...</Typography>
                                </TableCell>
                            </TableRow>
                        ) : overrides.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    <Typography>Geen override geschiedenis beschikbaar</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            overrides.map((override) => (
                                <TableRow key={override.id}>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatDateTime(override.timestamp)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={getStateLabel(override.state)} 
                                            color={getStateColor(override.state)} 
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Avatar 
                                                sx={{ 
                                                    width: 24, 
                                                    height: 24, 
                                                    fontSize: '0.75rem',
                                                    bgcolor: override.user_name ? 'primary.main' : 'grey.500'
                                                }}
                                            >
                                                {override.user_name ? getUserInitials(override.user_name) : <PersonIcon sx={{ fontSize: 16 }} />}
                                            </Avatar>
                                            <Tooltip title={override.user_email || 'Geen email beschikbaar'}>
                                                <Typography variant="body2" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {getUserDisplayName(override)}
                                                </Typography>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDateTime(override.created_at)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default OverrideHistory; 